import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { AllCommunityModule, IDatasource, ModuleRegistry, FilterModel, SortModelItem, ColDef } from 'ag-grid-community';
import { CursorPaginatedApiResponse } from '../../../../types/responses/cursor-paginated.response';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props<T extends unknown> extends AgGridReactProps<T> {
  rowLimit?: number;
  onGetRows: (params: {
    cursor: number | undefined;
    limit: number;
    filterModel?: FilterModel;
    filterQuery?: { [key: string]: any | undefined };
    sortModel?: SortModelItem[];
    sortQuery?: string[];
  }) => Promise<CursorPaginatedApiResponse<T>>;
}

const defaultLimit = 100;

export const InfniteScrollingGrid = <T extends unknown>({
  rowLimit = defaultLimit,
  onGetRows,
  onGridReady,
  ...props
}: Props<T>) => {
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const previousFilterModelRef = useRef<FilterModel | null>(null);
  const previousCursorRef = useRef<number | null | undefined>();
  const currentCursorRef = useRef<number | null | undefined>();
  const currentRowRef = useRef<number | undefined>();

  useEffect(() => {
    gridRef.current?.api?.refreshInfiniteCache();
  }, [location]);

  type NumberRangeFilter = { filter: string; filterTo: string };
  type DateRangeFilter = { dateFrom: string; dateTo?: string; type?: string };
  type TextFilter = { filter: string };

  const isNumberRangeFilter = (value: unknown): value is NumberRangeFilter =>
    typeof value === 'object' &&
    value !== null &&
    'filter' in value &&
    'filterTo' in value &&
    typeof value.filter === 'string' &&
    typeof value.filterTo === 'string';

  const isDateRangeFilter = (value: unknown): value is DateRangeFilter =>
    typeof value === 'object' && value !== null && 'dateFrom' in value && typeof value.dateFrom === 'string';

  const isTextFilter = (value: unknown): value is TextFilter =>
    typeof value === 'object' && value !== null && 'filter' in value && typeof value.filter === 'string';

  const getFilterValue = (value: unknown) => {
    if (isNumberRangeFilter(value)) {
      return { min: value.filter, max: value.filterTo };
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (isDateRangeFilter(value)) {
      if (!value.dateTo) {
        return {
          from: value.type === 'greaterThan' ? value.dateFrom : undefined,
          to: value.type === 'lessThan' ? value.dateFrom : undefined,
        };
      }
      return { from: value.dateFrom, to: value.dateTo };
    }

    if (isTextFilter(value)) {
      return value.filter;
    }

    return undefined;
  };

  const getRows: IDatasource['getRows'] = async ({
    startRow,
    successCallback,
    failCallback,
    filterModel,
    sortModel,
  }) => {
    if (currentRowRef.current && startRow > currentRowRef.current && currentCursorRef.current === null) {
      successCallback([], 0);
      return;
    }

    const isFilterCursorReset =
      currentCursorRef.current && JSON.stringify(filterModel) !== JSON.stringify(previousFilterModelRef.current);

    const rowCursor =
      (currentRowRef.current === startRow ? previousCursorRef.current : currentCursorRef.current) ?? undefined;

    try {
      setLoading(true);
      const { result, nextCursor } = await onGetRows({
        cursor: isFilterCursorReset ? 0 : rowCursor,
        limit: rowLimit,
        sortModel,
        sortQuery: sortModel?.map(({ colId, sort }) => (sort === 'desc' ? `-${colId}` : colId)),
        filterModel,
        filterQuery: Object.entries(filterModel).reduce((acc, [key, value]) => {
          const column = (props.columnDefs ?? []).find(
            ({ colId, field }: ColDef) => colId === key || field === key,
          ) as ColDef;

          if (!column) return acc;

          return {
            ...acc,
            [column?.field as string]: getFilterValue(value),
          };
        }, {}),
      });

      if (currentRowRef.current !== startRow || isFilterCursorReset) {
        previousCursorRef.current = isFilterCursorReset ? 0 : currentCursorRef.current;
        currentCursorRef.current = isFilterCursorReset ? 0 : nextCursor;
        currentRowRef.current = isFilterCursorReset ? 0 : startRow;
      }

      previousFilterModelRef.current = filterModel;
      successCallback(result, !nextCursor ? startRow + result.length : undefined);
      setLoading(false);
    } catch (error) {
      failCallback();
      throw error;
    }
  };

  return (
    <AgGridReact
      ref={gridRef}
      rowModelType={'infinite'}
      maxConcurrentDatasourceRequests={1}
      cacheBlockSize={rowLimit}
      defaultColDef={{ flex: 1, minWidth: 100, sortable: false }}
      blockLoadDebounceMillis={500}
      getRowId={(row) => row.data.id.toString()}
      onGridReady={(params) => {
        onGridReady?.(params);
        params.api.setGridOption('datasource', { getRows });
      }}
      {...props}
      loading={loading || props.loading}
    />
  );
};
